import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Trans, t } from '@grafana/i18n';
import { isFetchError, locationService } from '@grafana/runtime';
import {
  Alert,
  Box,
  Button,
  ConfirmModal,
  Field,
  Input,
  Label,
  LoadingPlaceholder,
  Space,
  Stack,
  TagsInput,
  Text,
  TextArea,
} from '@grafana/ui';
import { useAppNotification } from 'app/core/copy/appNotification';
import { EVENT_LOCATIONS } from 'app/features/dashboard/dashgrid/DashboardLibrary/constants';
import { CustomDashboardTemplateInteractions } from 'app/features/dashboard-scene/analytics/dashboard-templates/main';

import {
  useDeleteDashboardTemplateMutation,
  useGetDashboardTemplateQuery,
  useReplaceDashboardTemplateMutation,
} from '../api/clients/dashboardtemplates/v1alpha1';

import { buildDashboardTemplateBody } from './wire';

interface DashboardTemplateSettingsFormDTO {
  title: string;
  description: string;
  tags: string[];
}

export interface Props {
  dashboardTemplateUid: string;
}

function isConflictError(error: unknown) {
  return isFetchError(error) && error.status === 409;
}

export function DashboardTemplateSettingsForm({ dashboardTemplateUid }: Props) {
  const notifyApp = useAppNotification();

  const {
    data: templateResource,
    isLoading: isTemplateLoading,
    refetch,
  } = useGetDashboardTemplateQuery(dashboardTemplateUid ? { name: dashboardTemplateUid } : skipToken);

  const [replaceDashboardTemplate, { isLoading: isSaving, error }] = useReplaceDashboardTemplateMutation();
  const [deleteDashboardTemplate, { isLoading: isDeleting, error: deleteError }] = useDeleteDashboardTemplateMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isValid, isDirty },
  } = useForm<DashboardTemplateSettingsFormDTO>({
    mode: 'onBlur',
  });

  useEffect(() => {
    if (templateResource?.spec) {
      reset({
        title: templateResource.spec.title ?? '',
        description: templateResource.spec.description ?? '',
        tags: templateResource.spec.tags ?? [],
      });
    }
  }, [templateResource, reset]);

  const tags = watch('tags') ?? [];
  const isVersionConflict = isConflictError(error);

  const onSave = async (data: DashboardTemplateSettingsFormDTO, overwrite: boolean) => {
    let currentSpec = templateResource?.spec;
    let resourceVersion = templateResource?.metadata?.resourceVersion;

    if (overwrite) {
      const refreshed = await refetch().unwrap();
      currentSpec = refreshed.spec;
      resourceVersion = refreshed.metadata?.resourceVersion;
    }

    if (!currentSpec) {
      return;
    }

    try {
      await replaceDashboardTemplate({
        name: dashboardTemplateUid,
        dashboardTemplate: buildDashboardTemplateBody({
          name: dashboardTemplateUid,
          resourceVersion,
          spec: {
            title: data.title,
            description: data.description,
            tags: data.tags ?? [],
            dashboardVersion: currentSpec.dashboardVersion,
            dashboard: currentSpec.dashboard,
          },
        }),
      }).unwrap();

      CustomDashboardTemplateInteractions.updatedMetadata({
        status: 'completed',
        templateUid: dashboardTemplateUid,
        tagCount: data.tags?.length ?? 0,
        hasDescription: !!data.description,
      });

      notifyApp.success(t('dashboard-settings.template.saved', 'Template settings saved'));
    } catch (error) {
      if (isConflictError(error)) {
        CustomDashboardTemplateInteractions.updatedMetadata({ status: 'conflict', templateUid: dashboardTemplateUid });
      } else {
        CustomDashboardTemplateInteractions.updatedMetadata({
          status: 'failed',
          templateUid: dashboardTemplateUid,
          errorStatus: isFetchError(error) ? error.status : undefined,
        });
      }
      throw error;
    }
  };

  const onDelete = async () => {
    const result = await deleteDashboardTemplate({ name: dashboardTemplateUid });
    if ('error' in result) {
      // Leave the modal open so the user sees the error Alert and can retry / cancel.
      return;
    }
    CustomDashboardTemplateInteractions.deleted({
      templateUid: dashboardTemplateUid,
      eventLocation: EVENT_LOCATIONS.DASHBOARD_SETTINGS_PAGE,
    });
    setShowDeleteModal(false);
    notifyApp.success(t('dashboard-settings.template.deleted', 'Dashboard template deleted'));
    locationService.replace('/');
  };

  if (isTemplateLoading) {
    return <LoadingPlaceholder text={t('dashboard-settings.template.loading', 'Loading template settings...')} />;
  }

  if (!templateResource) {
    return (
      <Alert severity="error" title={t('dashboard-settings.template.load-failed-title', 'Failed to load template')}>
        <Trans i18nKey="dashboard-settings.template.load-failed-body">
          The template could not be loaded. Reload the page and try again.
        </Trans>
      </Alert>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit((data) => onSave(data, false))}>
        <Stack direction="column" gap={2}>
          <Field
            noMargin
            label={
              <Label htmlFor="template-title">
                <Trans i18nKey="dashboard-settings.template.title-label">Template title</Trans>
              </Label>
            }
            invalid={!!errors.title}
            error={errors.title?.message}
          >
            <Input
              id="template-title"
              {...register('title', {
                required: t('dashboard-settings.template.title-required', 'Required'),
              })}
              disabled={isSaving}
              aria-label={t('dashboard-settings.template.aria-label-title', 'Template title field')}
            />
          </Field>

          <Field
            noMargin
            label={
              <Label htmlFor="template-description">
                <Trans i18nKey="dashboard-settings.template.description-label">Template description</Trans>
              </Label>
            }
          >
            <TextArea
              id="template-description"
              {...register('description')}
              disabled={isSaving}
              aria-label={t('dashboard-settings.template.aria-label-description', 'Template description field')}
            />
          </Field>

          <Field
            noMargin
            label={
              <Label htmlFor="template-tags">
                <Trans i18nKey="dashboard-settings.template.tags-label">Template tags</Trans>
              </Label>
            }
          >
            <TagsInput
              id="template-tags"
              tags={tags}
              onChange={(value) => setValue('tags', value, { shouldDirty: true })}
              disabled={isSaving}
            />
          </Field>

          {isVersionConflict && (
            <Alert
              title={t('dashboard-settings.template.version-mismatch-title', 'Someone else has updated this template')}
              severity="error"
            >
              <p>
                <Trans i18nKey="dashboard-settings.template.version-mismatch-message">
                  Would you still like to save these template settings?
                </Trans>
              </p>
              <Box paddingTop={2}>
                <Stack alignItems="center">
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleSubmit((data) => onSave(data, true))}
                    disabled={isSaving}
                    icon={isSaving ? 'spinner' : undefined}
                  >
                    <Trans i18nKey="dashboard-settings.template.save-and-overwrite">Save and overwrite</Trans>
                  </Button>
                </Stack>
              </Box>
            </Alert>
          )}

          {!isVersionConflict && error !== undefined && (
            <Alert
              title={t('dashboard-settings.template.error-title', 'Failed to save template settings')}
              severity="error"
            >
              {error instanceof Error && <p>{error.message}</p>}
            </Alert>
          )}

          <Box paddingTop={2}>
            <Stack gap={2}>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDeleteModal(true)}
                disabled={isSaving || isDeleting}
              >
                <Trans i18nKey="dashboard-settings.template.delete-button">Delete template</Trans>
              </Button>
              <Button
                type="submit"
                disabled={!isValid || !isDirty || isSaving || isDeleting || isVersionConflict}
                icon={isSaving ? 'spinner' : undefined}
              >
                {isSaving ? (
                  <Trans i18nKey="dashboard-settings.template.saving">Saving...</Trans>
                ) : (
                  <Trans i18nKey="dashboard-settings.template.save">Save</Trans>
                )}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </form>
      {showDeleteModal && (
        <ConfirmModal
          isOpen={true}
          title={t('dashboard-settings.template.delete-modal-title', 'Delete')}
          body={
            <>
              <Text element="p">
                <Trans i18nKey="dashboard-settings.template.delete-modal-warning">
                  This action will permanently remove the entire dashboard template.
                </Trans>
              </Text>
              <Space v={1} />
              <Text element="p">
                <Trans i18nKey="dashboard-settings.template.delete-modal-prompt">
                  Do you want to delete this dashboard template?
                </Trans>
              </Text>
              {deleteError !== undefined && (
                <>
                  <Space v={2} />
                  <Alert
                    title={t('dashboard-settings.template.delete-error-title', 'Failed to delete template')}
                    severity="error"
                  >
                    {deleteError instanceof Error && <p>{deleteError.message}</p>}
                  </Alert>
                </>
              )}
            </>
          }
          confirmText={t('dashboard-settings.template.delete-modal-confirm', 'Delete')}
          confirmationText={t('dashboard-settings.template.delete-modal-confirmation', 'Delete')}
          onConfirm={onDelete}
          onDismiss={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
}
